/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

declare global {
  interface GrecaptchaEnterprise {
    ready(callback: () => void): void;
    execute(siteKey: string, options: { action: string }): Promise<string>;
  }

  interface Window {
    grecaptcha?: {
      enterprise?: GrecaptchaEnterprise;
    };
  }
}

const RECAPTCHA_SCRIPT_URL =
  'https://www.recaptcha.net/recaptcha/enterprise.js';

/**
 * Web implementation of RecaptchaManager using Google reCAPTCHA Enterprise v3.
 * Automatically loads the reCAPTCHA Enterprise script on creation.
 */
class WebRecaptchaManager {
  private readonly siteKey: string;
  private scriptReady: Promise<void>;

  constructor(siteKey: string) {
    this.siteKey = siteKey;
    this.scriptReady = this.loadScript();
  }

  initClient(): void {
    // Script loading starts in constructor — no-op here.
  }

  async execute(action: string): Promise<string> {
    await this.scriptReady;

    return new Promise((resolve, reject) => {
      if (typeof window !== 'undefined' && window.grecaptcha?.enterprise) {
        const recaptcha = window.grecaptcha.enterprise;
        recaptcha.ready(async () => {
          const token = await recaptcha.execute(this.siteKey, { action });
          if (!token) {
            reject(
              new Error(
                'reCAPTCHA execute returned an empty token. Verify that the site key is correct.',
              ),
            );
            return;
          }
          resolve(token);
        });
        return;
      }
      reject(
        new Error(
          'reCAPTCHA Enterprise is not available. The script may have failed to load.',
        ),
      );
    });
  }

  private loadScript(): Promise<void> {
    if (typeof window === 'undefined') return Promise.resolve();

    if (window.grecaptcha?.enterprise) return Promise.resolve();

    return new Promise<void>((resolve, reject) => {
      const script = document.createElement('script');
      script.src = `${RECAPTCHA_SCRIPT_URL}?render=${this.siteKey}`;
      script.async = true;
      script.onload = () => {
        const style = document.createElement('style');
        style.innerHTML = '.grecaptcha-badge{visibility: hidden;}';
        document.head.appendChild(style);
        resolve();
      };
      script.onerror = () =>
        reject(new Error('Failed to load reCAPTCHA Enterprise script.'));
      document.head.appendChild(script);
    });
  }
}

export const createRecaptchaManager = (siteKey: string) => {
  return new WebRecaptchaManager(siteKey);
};
